import React, { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';

export interface WeeklyRating {
    week: string; // e.g., "Dec 9" or "W1", "W2", etc.
    weekNumber: number; // 1-13 for ordering
    rating: number; // 0-10 scale
    date: Date; // Full date for tooltip
}

interface WeeklyRatingChartProps {
    data: WeeklyRating[];
    height?: number;
    lineColor?: string;
    showGrid?: boolean;
}

const WeeklyRatingChart: React.FC<WeeklyRatingChartProps> = ({
    data,
    height = 200,
    lineColor = '#8FBC8F',
    showGrid = true
}) => {
    // Sort data by weekNumber to ensure proper order
    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => a.weekNumber - b.weekNumber);
    }, [data]);

    // Track selected data point
    const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

    // Calculate average for reference line
    const averageRating = useMemo(() => {
        if (sortedData.length === 0) return 0;
        const sum = sortedData.reduce((acc, item) => acc + item.rating, 0);
        return Number((sum / sortedData.length).toFixed(1));
    }, [sortedData]);

    // Get displayed rating - either selected or latest
    const displayedData = selectedIndex !== null
        ? sortedData[selectedIndex]
        : sortedData[sortedData.length - 1];
    const displayedRating = displayedData?.rating ?? 0;
    const displayedLabel = selectedIndex !== null
        ? displayedData?.week
        : 'Current Rating';

    // Custom dot for the line - larger for better touch targets
    const CustomDot = (props: any) => {
        const { cx, cy, index } = props;
        const isLast = index === sortedData.length - 1;
        const isSelected = index === selectedIndex;
        // Only highlight the last dot if nothing is selected
        const shouldHighlight = isSelected || (isLast && selectedIndex === null);

        return (
            <circle
                cx={cx}
                cy={cy}
                r={shouldHighlight ? 7 : 4}
                fill={lineColor}
                stroke={shouldHighlight ? '#ffffff' : 'transparent'}
                strokeWidth={shouldHighlight ? 2 : 0}
                style={{ cursor: 'pointer' }}
                onClick={() => setSelectedIndex(index === selectedIndex ? null : index)}
            />
        );
    };

    if (sortedData.length === 0) {
        return (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                No weekly data available
            </div>
        );
    }

    return (
        <div className="w-full">
            {/* Header with current rating */}
            <div className="flex items-center justify-between mb-3">
                <div>
                    <p className="text-white/70 text-xs">{displayedLabel}</p>
                    <p className="text-white font-bold text-2xl">{displayedRating.toFixed(1)}</p>
                </div>
                <div className="text-right">
                    <p className="text-white/70 text-xs">13-Week Avg</p>
                    <p className="text-white font-semibold text-lg">{averageRating}</p>
                </div>
            </div>

            {/* Chart */}
            <ResponsiveContainer width="100%" height={height}>
                <LineChart data={sortedData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    {showGrid && (
                        <CartesianGrid
                            strokeDasharray="3 3"
                            stroke="rgba(255,255,255,0.1)"
                            vertical={false}
                        />
                    )}
                    <XAxis
                        dataKey="week"
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                        tickLine={false}
                        interval="preserveStartEnd"
                    />
                    <YAxis
                        domain={[0, 10]}
                        ticks={[0, 2.5, 5, 7.5, 10]}
                        tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 10 }}
                        axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                        tickLine={false}
                    />
                    <ReferenceLine
                        y={averageRating}
                        stroke="rgba(255,255,255,0.3)"
                        strokeDasharray="5 5"
                        label={{
                            value: 'Avg',
                            position: 'right',
                            fill: 'rgba(255,255,255,0.5)',
                            fontSize: 10
                        }}
                    />
                    <Line
                        type="monotone"
                        dataKey="rating"
                        stroke={lineColor}
                        strokeWidth={3}
                        dot={<CustomDot />}
                        activeDot={false}
                    />
                </LineChart>
            </ResponsiveContainer>
        </div>
    );
};

export default WeeklyRatingChart;
